import React, { SyntheticEvent, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';

import { Alert, Chip, CircularProgress, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetChainById } from '~/hooks/apis/reservation/useGetChainById';

import ChainRatePlansTab from './components/ChainRatePlansTab';
import ChainRateRulesTab from './components/ChainRateRulesTab';
import ChainVendorsTab from './components/ChainVendorsTab';

const TAB_NAMES = ['vendors', 'rate-plans', 'rate-rules'] as const;

export default function ChainDetailPage() {
  const { chainId } = useParams<{ chainId: string }>();
  const history = useHistory();
  const { hash } = useLocation();

  const hashTab = hash.replace('#', '');
  const initialTab = TAB_NAMES.indexOf(hashTab as (typeof TAB_NAMES)[number]);
  const [selectedTab, setSelectedTab] = useState(initialTab >= 0 ? initialTab : 0);

  const { data: chain, isLoading, isError } = useGetChainById(chainId);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    history.replace({ hash: TAB_NAMES[newValue] });
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <CircularProgress />
      </Container>
    );
  }

  if (isError || !chain) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Chain not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{chain.name} — Chain</title>
      </Helmet>
      <PageHeader title={chain.name} backButton="/vendors/chains">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {chain.sfid}
          </Typography>
          <Chip
            label={chain.is_active ? 'Active' : 'Inactive'}
            color={chain.is_active ? 'success' : 'default'}
            size="small"
          />
        </Stack>
      </PageHeader>

      <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Vendors" />
        <Tab label="Rate Plans" />
        <Tab label="Rate Rules" />
      </Tabs>

      {selectedTab === 0 && <ChainVendorsTab chainId={chainId} />}
      {selectedTab === 1 && <ChainRatePlansTab chainSfid={chain.sfid} />}
      {selectedTab === 2 && <ChainRateRulesTab chainId={chainId} />}
    </Container>
  );
}
